import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { log } from '@/lib/log';

const AuthContext = createContext(null);

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function fetchProfileDirect(userId, accessToken) {
    const token = accessToken;
    const url = `${SUPABASE_URL}/rest/v1/profiles?id=eq.${userId}&select=id,email,full_name,is_super_admin,preferred_language`;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
        const r = await fetch(url, {
            headers: { apikey: SUPABASE_ANON, Authorization: `Bearer ${token || SUPABASE_ANON}` },
            signal: controller.signal,
        });
        clearTimeout(timeout);
        if (!r.ok) return null;
        const rows = await r.json();
        return Array.isArray(rows) && rows.length ? rows[0] : null;
    } catch (e) {
        log.warn('AuthContext: profile fetch error', e?.message || e);
        return null;
    }
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const sessionRef = useRef(null);

    const loadProfile = async (userId, accessToken) => {
        const tk = accessToken || sessionRef.current?.access_token;
        const row = await fetchProfileDirect(userId, tk);
        setProfile(row);
    };

    useEffect(() => {
        let mounted = true;
        const bootstrap = async () => {
            const { data: { session: sess } } = await supabase.auth.getSession();
            if (!mounted) return;
            sessionRef.current = sess;
            setSession(sess);
            setUser(sess?.user ?? null);
            if (sess?.user) await loadProfile(sess.user.id, sess.access_token);
            setLoading(false);
        };
        bootstrap();

        const { data: sub } = supabase.auth.onAuthStateChange(async (_evt, sess) => {
            sessionRef.current = sess;
            setSession(sess);
            setUser(sess?.user ?? null);
            if (sess?.user) await loadProfile(sess.user.id, sess.access_token);
            else setProfile(null);
        });
        return () => { mounted = false; sub.subscription.unsubscribe(); };
        // eslint-disable-next-line
    }, []);

    const signUp = async ({ email, password, fullName }) => {
        const { data, error } = await supabase.auth.signUp({
            email, password, options: { data: { full_name: fullName } },
        });
        return { data, error };
    };

    const signIn = async ({ email, password }) => {
        try {
            const { data, error } = await supabase.auth.signInWithPassword({ email, password });
            return { data, error };
        } catch (err) {
            return { data: null, error: { message: err?.message || 'Sign-in failed' } };
        }
    };

    const signOut = async () => {
        await supabase.auth.signOut();
        setProfile(null);
    };

    const refreshProfile = async () => {
        if (user?.id) await loadProfile(user.id, sessionRef.current?.access_token);
    };

    return (
        <AuthContext.Provider value={{ user, session, profile, loading, signUp, signIn, signOut, refreshProfile }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
